import { Cart } from 'shared/types/cart';

/**
 * Legacy per-row discount sum, kept only as a fallback for carts that have no platform total
 * (`sum`) yet. It reads product discounts (price vs discountedPrice) and the total-price cart
 * discount (cart.discountedAmount), but is blind to item-level and shipping-level cart
 * discounts — which is why it is no longer the primary path.
 */
const legacyDiscountedAmount = (cart?: Partial<Cart>) => {
  return (
    (cart?.discountedAmount?.centAmount || 0) +
    (cart?.lineItems ?? []).reduce((acc, curr) => {
      if (curr.isGift) {
        return acc;
      }

      // Use discountedPrice instead of totalPrice
      if (curr.discountedPrice) {
        const originalAmount = (curr.price?.centAmount || 0) * (curr.count as number);
        const discountedAmount = (curr.discountedPrice?.centAmount || 0) * (curr.count as number);
        const discount = originalAmount - discountedAmount;

        return acc + discount;
      }

      return acc;
    }, 0) +
    (() => {
      const mergedCustomLineItemIds = new Set(
        (cart?.lineItems ?? []).map((item) => item.customLineItemId).filter(Boolean),
      );

      return (cart?.customLineItems ?? [])
        .filter((item) => !mergedCustomLineItemIds.has(item.id))
        .reduce((acc, curr) => {
          if (curr.originalPrice && curr.money) {
            const originalAmount =
              (curr.originalPrice.centAmount || 0) * (curr.quantity as number);
            const currentAmount =
              (curr.money.centAmount || 0) * (curr.quantity as number);
            const discount = originalAmount - currentAmount;

            return acc + (discount > 0 ? discount : 0);
          }

          return acc;
        }, 0);
    })()
  );
};

/**
 * The full list-price value of the cart's display rows: Σ price × count over line items plus
 * unmerged custom line items — with NO tax adjustment, because it is compared against the
 * platform total, which includes any tax that is included in prices. Custom line items already
 * merged into a display row (finishes) are skipped, so split rows can never double count.
 */
const listValueAmount = (cart?: Partial<Cart>) => {
  const lineItemsValue = (cart?.lineItems ?? []).reduce((acc, curr) => {
    if (curr.isGift) return acc;
    return acc + (curr.price?.centAmount || 0) * (curr.count as number);
  }, 0);

  const mergedCustomLineItemIds = new Set(
    (cart?.lineItems ?? []).map((item) => item.customLineItemId).filter(Boolean),
  );

  const customLineItemsValue = (cart?.customLineItems ?? [])
    .filter((item) => !mergedCustomLineItemIds.has(item.id))
    .reduce((acc, curr) => acc + ((curr.originalPrice ?? curr.money)?.centAmount || 0) * (curr.quantity as number), 0);

  return lineItemsValue + customLineItemsValue;
};

/**
 * The shipping amount actually charged: the base rate minus any shipping-level cart discounts.
 * This is what the summary's shipping row shows, so free-shipping codes read as
 * "Est. Shipping $0.00" rather than hiding inside the Discount figure.
 */
export const discountedShippingAmount = (cart?: Partial<Cart>) => {
  const basePrice = cart?.shippingInfo?.price?.centAmount ?? 0;
  const shippingDiscount = (cart?.shippingInfo?.discounts ?? []).reduce(
    (acc, discount) => acc + (discount.discountedAmount?.centAmount || 0),
    0,
  );
  return Math.max(0, basePrice - shippingDiscount);
};

/**
 * Total savings on the cart's merchandise, in cents.
 *
 * Derived from platform values rather than re-summed per discount type: the cart's list-price
 * value (line items at list price + the shipping actually charged) minus what commercetools
 * actually charges (`sum`, the platform totalPrice, which is net of every discount). This makes
 * product discounts, item-level cart discounts and total-price discounts all surface in the one
 * Discount figure — the per-type enumeration this replaced silently dropped item-level cart
 * discounts (so a 100%-off code showed a full-price cart). Shipping savings are deliberately
 * excluded: they render on the shipping row itself (see discountedShippingAmount), and counting
 * them here as well would double-subtract from the total.
 */
export const calculateDiscountedAmount = (cart?: Partial<Cart>) => {
  // No platform total *at all* — fall back to the per-row sum rather than reporting no discount.
  // A total of zero is a real, fully discounted cart and must take the derivation below.
  if (cart?.sum?.centAmount === undefined) {
    return legacyDiscountedAmount(cart);
  }

  const discount = listValueAmount(cart) + discountedShippingAmount(cart) - cart.sum.centAmount;

  // A cart can't have negative savings; clamp so data quirks never render "Discount +$x".
  return Math.max(0, discount);
};
